// briefStore.js — persistence for Intelligence Briefs and per-user prefs.
//
// MVP: everything lives in localStorage. Briefs are text, so size is not a
// concern at real-world volumes (a year of daily briefs is well under 1MB).
// When the user signs in we do NOT upload briefs to Supabase yet; that is
// deliberately deferred so we can ship the feature end-to-end first and add
// cross-device sync as a follow-up.
//
// User scoping:
//   Keys are namespaced by active user id so switching accounts on the same
//   device never leaks one user's briefs to another. Call setUser(userId)
//   from the auth hook whenever the session changes. 'guest' is the default
//   bucket used before sign-in.
//
// Keys:
//   proxera.briefs.v1:<userId>       -> array of IntelligenceBrief records
//   proxera.briefPrefs.v1:<userId>   -> UserPreferences record
// ──────────────────────────────────────────────────────────────────────────

import { v4 as uuidv4 } from 'uuid';

const BRIEFS_PREFIX = 'proxera.briefs.v1';
const PREFS_PREFIX  = 'proxera.briefPrefs.v1';

// Keyword similarity used to decide whether two custom focuses are "the same
// concept" for the purposes of the one-per-day dedup. Returns a Jaccard
// coefficient between 0 and 1 over a normalized, stopworded token set.
// Standard bundles (technology, finance, etc.) ignore this entirely and
// dedup by bundle alone.
const STOPWORDS = new Set([
  'a','an','the','and','or','but','of','in','on','for','to','from','with',
  'about','into','over','after','before','at','by','is','are','was','were',
  'be','been','being','this','that','these','those','it','its','as','than',
  'then','so','also','more','most','very','just','only','some','any','all',
  'me','my','our','we','you','your','i','not','no','can','could','should',
  'would','will','may','might','do','does','done','have','has','had','new',
  'news','update','updates','latest','today','todays','briefing','brief',
]);
function tokenize(str) {
  if (!str || typeof str !== 'string') return [];
  return str
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s'-]/gu, ' ')
    .split(/\s+/)
    .map(t => t.replace(/^['-]+|['-]+$/g, ''))
    .filter(t => t.length >= 2 && !STOPWORDS.has(t));
}
function sameCustomConcept(a, b) {
  // Both empty → trivially the same (default custom generation).
  const ta = new Set(tokenize(a || ''));
  const tb = new Set(tokenize(b || ''));
  if (ta.size === 0 && tb.size === 0) return true;
  if (ta.size === 0 || tb.size === 0) return false;
  let intersect = 0;
  for (const t of ta) if (tb.has(t)) intersect++;
  const union = ta.size + tb.size - intersect;
  const jaccard = union === 0 ? 0 : intersect / union;
  // Threshold chosen empirically: 0.5 lets "climate policy" and "climate
  // policy update" dedup (1 shared / 2 union after stopwording = 0.5),
  // but "climate policy" vs "tech layoffs" stays distinct (0 / 4 = 0).
  return jaccard >= 0.5;
}

// Current active user id. Set via briefStore.setUser() from the auth hook.
// Default to 'guest' so the app still works before the first auth event.
let activeUserId = 'guest';

function briefsKey() { return `${BRIEFS_PREFIX}:${activeUserId}`; }
function prefsKey()  { return `${PREFS_PREFIX}:${activeUserId}`; }

// Legacy un-namespaced keys from before user scoping existed. Migrated once
// on first setUser() call so pre-update briefs are not lost.
const LEGACY_BRIEFS_KEY = BRIEFS_PREFIX;
const LEGACY_PREFS_KEY  = PREFS_PREFIX;

const DEFAULT_PREFS = {
  defaultBundle: 'technology',
  customBundles: [],           // [{ name, prompt }]
  voicePreference: 'pNInz6obpgDQGcFmaJgB', // Adam
  playbackSpeed: 1,
  autoDeleteAfterDays: null,   // null = never
};

function readBriefs() {
  try {
    const raw = localStorage.getItem(briefsKey());
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch { return []; }
}

function writeBriefs(arr) {
  try { localStorage.setItem(briefsKey(), JSON.stringify(arr)); } catch {}
}

function readPrefs() {
  try {
    const raw = localStorage.getItem(prefsKey());
    if (!raw) return { ...DEFAULT_PREFS };
    const parsed = JSON.parse(raw);
    return { ...DEFAULT_PREFS, ...(parsed || {}) };
  } catch { return { ...DEFAULT_PREFS }; }
}

function writePrefs(p) {
  try { localStorage.setItem(prefsKey(), JSON.stringify(p)); } catch {}
}

// Auto-delete briefs older than the user's chosen retention window. Called
// from listBriefs so the cleanup happens lazily without a background timer.
function applyAutoDelete(arr, days) {
  if (days == null || typeof days !== 'number' || days <= 0) return arr;
  const cutoff = Date.now() - days * 24 * 60 * 60 * 1000;
  return arr.filter(b => {
    const t = Date.parse(b.generatedAt || '');
    return isFinite(t) ? t >= cutoff : true;
  });
}

// One-time migration of legacy un-namespaced keys into the new per-user
// bucket. Runs the first time setUser() sees a real user id so pre-update
// briefs come along to the account. Legacy keys are cleared afterward so
// a future sign-out does not leave them lying around.
function migrateLegacyIfNeeded(userId) {
  if (!userId || userId === 'guest') return;
  try {
    const legacyBriefs = localStorage.getItem(LEGACY_BRIEFS_KEY);
    const legacyPrefs  = localStorage.getItem(LEGACY_PREFS_KEY);
    if (legacyBriefs && !localStorage.getItem(`${BRIEFS_PREFIX}:${userId}`)) {
      localStorage.setItem(`${BRIEFS_PREFIX}:${userId}`, legacyBriefs);
    }
    if (legacyPrefs && !localStorage.getItem(`${PREFS_PREFIX}:${userId}`)) {
      localStorage.setItem(`${PREFS_PREFIX}:${userId}`, legacyPrefs);
    }
    localStorage.removeItem(LEGACY_BRIEFS_KEY);
    localStorage.removeItem(LEGACY_PREFS_KEY);
  } catch {}
}

export const briefStore = {
  // ---- User scoping ----
  // Called from useAuth when the session changes. Null / falsy → 'guest'.
  setUser(userId) {
    const next = userId || 'guest';
    if (next === activeUserId) return;
    activeUserId = next;
    migrateLegacyIfNeeded(activeUserId);
  },
  currentUser() { return activeUserId; },

  // ---- Prefs ----
  getPrefs() { return readPrefs(); },
  updatePrefs(patch) {
    const next = { ...readPrefs(), ...(patch || {}) };
    writePrefs(next);
    return next;
  },

  // ---- Briefs ----
  listBriefs() {
    const prefs = readPrefs();
    const cleaned = applyAutoDelete(readBriefs(), prefs.autoDeleteAfterDays);
    // Persist the post-cleanup list so future reads are already trimmed.
    if (cleaned.length !== readBriefs().length) writeBriefs(cleaned);
    // Newest first.
    return [...cleaned].sort((a, b) => String(b.generatedAt).localeCompare(String(a.generatedAt)));
  },

  // Return the brief generated today (UTC day) for the given bundle, if any.
  // Used to short-circuit re-generation and flip the CTA to "View today's".
  //
  // For the Custom bundle, the user may make multiple briefs per day as long
  // as each one is on a meaningfully different topic. We therefore accept an
  // optional customFocus arg: if provided, we only return an existing brief
  // whose stored focus is the same concept (Jaccard ≥ 0.5 after stopwording).
  // For standard bundles, customFocus is ignored.
  todayForBundle(bundleId, customFocus) {
    const today = new Date().toISOString().slice(0, 10);
    const todays = readBriefs().filter(b => b.bundle === bundleId && (b.generatedAt || '').slice(0, 10) === today);
    if (todays.length === 0) return null;
    if (bundleId !== 'custom') return todays[0];
    // Custom: look for a same-concept match on stored customTopicName.
    const match = todays.find(b => sameCustomConcept(b.customTopicName || '', customFocus || ''));
    return match || null;
  },

  // Return the most recent brief (any day) for a bundle, for the purpose of
  // tightening the web_search window in the next generation. We want "only
  // find stories since the last edition" behavior so consecutive briefs do
  // not overlap on 6 of 7 days' worth of sources.
  //
  // Custom bundle uses same-concept matching on customTopicName so the
  // user's "AI chips" thread does not tighten against their "climate" thread.
  latestBriefForBundle(bundleId, customFocus) {
    const all = readBriefs();
    const matches = all.filter(b => {
      if (b.bundle !== bundleId) return false;
      if (bundleId !== 'custom') return true;
      return sameCustomConcept(b.customTopicName || '', customFocus || '');
    });
    if (matches.length === 0) return null;
    return matches
      .slice()
      .sort((a, b) => String(b.generatedAt).localeCompare(String(a.generatedAt)))[0];
  },

  get(id) {
    return readBriefs().find(b => b.id === id) || null;
  },

  // Save a fresh brief.
  //
  // Dedup rules for the "one per day" contract:
  //   - Standard bundles (technology, finance, ...): replace any existing
  //     brief with the same (bundle, day).
  //   - Custom bundle: only replace an existing custom brief from today if
  //     its stored focus is the same concept (Jaccard ≥ 0.5 after
  //     stopwording). This lets the user generate multiple distinct custom
  //     briefs per day — one on "climate policy", another on "AI chips" —
  //     without the store clobbering either.
  save(brief) {
    const now = new Date().toISOString();
    const next = {
      id: brief.id || uuidv4(),
      generatedAt: brief.generatedAt || now,
      bundle: brief.bundle,
      customTopicName: brief.customTopicName || null,
      title: brief.title || 'THE BRIEFING',
      edition: brief.edition || 1,
      date: brief.date || now.slice(0, 10),
      insights: Array.isArray(brief.insights) ? brief.insights : [],
      audioFilePath: brief.audioFilePath || null,
      audioGeneratedAt: brief.audioGeneratedAt || null,
    };
    const today = next.generatedAt.slice(0, 10);
    const all = readBriefs().filter(b => {
      const sameDay = (b.generatedAt || '').slice(0, 10) === today;
      const sameBundle = b.bundle === next.bundle;
      if (!sameDay || !sameBundle) return true; // keep
      if (next.bundle !== 'custom') return false; // standard: drop same-bundle same-day
      // Custom: drop only if the topics are the same concept.
      return !sameCustomConcept(b.customTopicName || '', next.customTopicName || '');
    });
    all.push(next);
    writeBriefs(all);
    return next;
  },

  update(id, patch) {
    const all = readBriefs();
    const idx = all.findIndex(b => b.id === id);
    if (idx === -1) return null;
    all[idx] = { ...all[idx], ...patch };
    writeBriefs(all);
    return all[idx];
  },

  remove(id) {
    const all = readBriefs().filter(b => b.id !== id);
    writeBriefs(all);
  },

  // Pick the next edition number for the given bundle. We count the user's
  // prior briefs for this bundle and add one, so editions are meaningful.
  nextEditionFor(bundleId) {
    const count = readBriefs().filter(b => b.bundle === bundleId).length;
    return count + 1;
  },
};

// ── Display-only edition numbering ─────────────────────────────────────
//
// The raw `brief.edition` field is the one the LLM saw when generating
// the brief — it's meaningful in prompts, narration, and the source-window
// logic. What it is NOT meaningful for is the visible sequence. If a
// generation fails mid-flight and gets rolled back, or if the user ever
// deletes one, the surviving briefs can end up labeled "Edition 1, 3, 4"
// — a gap that implies the reader missed an issue they never missed.
//
// Fix: anywhere we RENDER an edition number, pass the brief through this
// helper. It ranks the bundle's surviving briefs by generatedAt (oldest
// first) and returns a 1-based position. Storage is unchanged — the raw
// edition field still drives everything that depends on it.
export function displayEditionFor(brief, allBriefs) {
  if (!brief) return 1;
  const bundleId = brief.bundle;
  const siblings = (allBriefs || [])
    .filter(b => b && b.bundle === bundleId)
    .slice()
    .sort((a, b) => {
      const at = String(a.generatedAt || a.date || '');
      const bt = String(b.generatedAt || b.date || '');
      if (at < bt) return -1;
      if (at > bt) return 1;
      // Tie-breaker on id so the order is stable across renders.
      return String(a.id || '').localeCompare(String(b.id || ''));
    });
  const idx = siblings.findIndex(b => b.id === brief.id);
  return idx >= 0 ? idx + 1 : (brief.edition || 1);
}

